import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Rocket, Layout, Megaphone, PartyPopper,
  ArrowLeft, ArrowRight, Trash2, Pencil, Sparkles, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { confirmAction } from "@/lib/confirm";
import { errMsg } from "@/lib/errors";

// Map built-in template ids to icons for the gallery
const BUILTIN_ICONS = {
  builtin_product_launch: Rocket,
  builtin_website_redesign: Layout,
  builtin_event: PartyPopper,
  builtin_marketing_campaign: Megaphone,
};

function formatToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

/**
 * Two-step modal.
 * Step "list": show all templates (built-in + user). Click one → step "config".
 * Step "config": ask new project name + start date, then POST /projects/from-template/{id}.
 */
export default function TemplateGalleryModal({
  open,
  onOpenChange,
  onProjectCreated,
  onOpenSave, // (optional) called when the user wants to reach the "Save as template" flow from empty state
}) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState("list"); // 'list' | 'config'
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [startDate, setStartDate] = useState(formatToday());
  const [creating, setCreating] = useState(false);

  const builtins = useMemo(() => templates.filter((t) => t.is_builtin), [templates]);
  const mine = useMemo(() => templates.filter((t) => !t.is_builtin), [templates]);

  useEffect(() => {
    if (!open) return;
    setStep("list");
    setSelected(null);
    setProjectName("");
    setStartDate(formatToday());
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/templates");
        setTemplates(data);
      } catch (_) {
        toast.error(t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, t]);

  const handlePick = (tpl) => {
    setSelected(tpl);
    setProjectName(tpl.name);
    setStep("config");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selected || !projectName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/from-template/${selected.template_id}`, {
        name: projectName.trim(),
        start_date: startDate,
      });
      toast.success(t("templates.createdToast", { count: selected.task_count }));
      onProjectCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      if (err.response?.status === 402) {
        // Global paywall listener already popped the modal — just close.
        onOpenChange(false);
      } else {
        toast.error(errMsg(err, t("common.error")));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (tpl, e) => {
    e.stopPropagation();
    if (!(await confirmAction({ message: t("templates.confirmDelete", { name: tpl.name }), destructive: true }))) return;
    try {
      await api.delete(`/templates/${tpl.template_id}`);
      setTemplates((ts) => ts.filter((x) => x.template_id !== tpl.template_id));
      toast.success(t("templates.deletedToast"));
    } catch (err) {
      toast.error(errMsg(err, t("common.error")));
    }
  };

  const handleRename = async (tpl, e) => {
    e.stopPropagation();
    const next = window.prompt(t("templates.renamePrompt"), tpl.name);
    if (!next || next.trim() === tpl.name) return;
    try {
      const { data } = await api.patch(`/templates/${tpl.template_id}`, {
        name: next.trim(),
      });
      setTemplates((ts) => ts.map((x) => (x.template_id === data.template_id ? data : x)));
      toast.success(t("templates.renamedToast"));
    } catch (err) {
      toast.error(errMsg(err, t("common.error")));
    }
  };

  const Card = ({ tpl }) => {
    const Icon = BUILTIN_ICONS[tpl.template_id] || Sparkles;
    const isSelected = selected?.template_id === tpl.template_id;
    return (
      <button
        type="button"
        data-testid={`template-card-${tpl.template_id}`}
        onClick={() => handlePick(tpl)}
        className={`group relative text-left border rounded-sm p-4 hover:border-neutral-950 hover:shadow-sm transition-all ${
          isSelected ? "border-[#E05236] bg-[#E05236]/5" : "border-stone-200"
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-sm grid place-items-center text-white shrink-0"
            style={{ background: tpl.color }}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm leading-tight tracking-tight truncate">
              {tpl.name}
            </div>
            <div className="text-xs text-neutral-500 mt-1 line-clamp-2 min-h-[2rem]">
              {tpl.description || t("templates.noDescription")}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t("templates.tasksCount", { count: tpl.task_count })}
              </span>
              {tpl.is_builtin && (
                <span
                  data-testid={`template-builtin-${tpl.template_id}`}
                  className="inline-flex items-center gap-1 text-[#E05236]"
                >
                  <Sparkles className="w-3 h-3" />
                  {t("templates.builtin")}
                </span>
              )}
            </div>
          </div>
        </div>
        {!tpl.is_builtin && (
          <div className="absolute top-2 right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <span
              role="button"
              onClick={(e) => handleRename(tpl, e)}
              data-testid={`template-rename-${tpl.template_id}`}
              className="p-1 rounded-sm hover:bg-stone-100 text-neutral-500 hover:text-neutral-950 cursor-pointer"
            >
              <Pencil className="w-3.5 h-3.5" />
            </span>
            <span
              role="button"
              onClick={(e) => handleDelete(tpl, e)}
              data-testid={`template-delete-${tpl.template_id}`}
              className="p-1 rounded-sm hover:bg-red-50 text-neutral-400 hover:text-red-600 cursor-pointer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </span>
          </div>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="template-gallery-modal"
        className="rounded-sm max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {step === "list" ? t("templates.galleryTitle") : t("templates.configureTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-600">
            {step === "list" ? t("templates.galleryHint") : t("templates.configureHint")}
          </DialogDescription>
        </DialogHeader>

        {step === "list" && (
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {loading && (
              <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 py-8 text-center">
                {t("common.loading")}
              </div>
            )}
            {!loading && (
              <>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mt-2 mb-3">
                  {t("templates.builtinSection")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {builtins.map((tpl) => (
                    <Card key={tpl.template_id} tpl={tpl} />
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-semibold mt-8 mb-3">
                  {t("templates.mineSection")}
                </div>
                {mine.length === 0 ? (
                  <div
                    data-testid="template-mine-empty"
                    className="border border-dashed border-stone-300 rounded-sm p-6 text-sm text-neutral-500 text-center"
                  >
                    {t("templates.mineEmpty")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-2">
                    {mine.map((tpl) => (
                      <Card key={tpl.template_id} tpl={tpl} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === "config" && selected && (
          <form onSubmit={handleCreate} className="space-y-5 pt-2 flex-1 overflow-y-auto">
            <div className="border border-stone-200 rounded-sm p-4 flex items-start gap-3 bg-stone-50">
              <div
                className="w-9 h-9 rounded-sm shrink-0"
                style={{ background: selected.color }}
              />
              <div>
                <div className="font-display font-bold text-base tracking-tight">
                  {selected.name}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {t("templates.tasksCount", { count: selected.task_count })}
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="pft-name" className="text-xs uppercase tracking-[0.15em]">
                {t("templates.newProjectName")}
              </Label>
              <Input
                id="pft-name"
                data-testid="pft-name-input"
                autoFocus
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
                className="mt-2 h-11 rounded-sm"
              />
            </div>
            <div>
              <Label htmlFor="pft-start" className="text-xs uppercase tracking-[0.15em]">
                {t("templates.startDate")}
              </Label>
              <Input
                id="pft-start"
                data-testid="pft-start-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="mt-2 h-11 rounded-sm"
                lang={i18n.language}
              />
              <p className="text-xs text-neutral-500 mt-1.5">
                {t("templates.startDateHint")}
              </p>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep("list")}
                data-testid="pft-back-btn"
                className="rounded-sm text-xs uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                {t("common.back")}
              </Button>
              <Button
                type="submit"
                disabled={creating || !projectName.trim()}
                data-testid="pft-create-btn"
                className="rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-11 px-5"
              >
                {creating ? "..." : t("templates.createProject")}
                {!creating && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
